import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { signupUser, signinUser, getCurrentUser } from '../apiClient';

const AuthContext = createContext(null);

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE = 2 * 60 * 1000; // warn 2 min before signout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('buck_euchre_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [dailyRewardToast, setDailyRewardToast] = useState(null); // { amount, streak }
  const idleTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  // Check for existing session on mount
  useEffect(() => {
    async function checkAuth() {
      if (token) {
        try {
          const result = await getCurrentUser(token);
          if (result && result.user) {
            setUser(result.user);
          } else {
            // Invalid token, clear it
            localStorage.removeItem('buck_euchre_token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('buck_euchre_token');
          setToken(null);
        }
      }
      setIsLoading(false);
    }
    checkAuth();
  }, [token]);

  const signout = useCallback(() => {
    localStorage.removeItem('buck_euchre_token');
    setToken(null);
    setUser(null);
    setShowIdleWarning(false);
  }, []);

  // Idle timer - auto sign-out after 30 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      clearTimeout(idleTimeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
      setShowIdleWarning(false);

      warningTimeoutRef.current = setTimeout(() => {
        setShowIdleWarning(true);
      }, IDLE_TIMEOUT - WARNING_BEFORE);

      idleTimeoutRef.current = setTimeout(() => {
        signout();
      }, IDLE_TIMEOUT);
    };

    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(idleTimeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
    };
  }, [user, signout]);

  const signup = async (email, password, firstName, lastName, nickname, zipCode, city, state, referralCode) => {
    const result = await signupUser(email, password, firstName, lastName, nickname, zipCode, city, state, referralCode);
    if (result.success) {
      localStorage.setItem('buck_euchre_token', result.token);
      setToken(result.token);
      setUser(result.user);
    }
    return result;
  };

  const signin = async (email, password) => {
    const result = await signinUser(email, password);
    if (result.success) {
      localStorage.setItem('buck_euchre_token', result.token);
      setToken(result.token);
      setUser(result.user);

      // Show daily login drip toast (+5 Bucks, once per calendar day)
      if (result.dailyDrip && result.dailyDrip.amount > 0) {
        setDailyRewardToast({ amount: result.dailyDrip.amount, streak: 0 });
        setTimeout(() => setDailyRewardToast(null), 5000);
      }
    }
    return result;
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const isClubMember = user?.membershipTier === 'club_member';
  const isPremium = isClubMember; // backward compat alias
  const tierFeatures = {
    free: { dailyDrip: 5, adFree: false, roomCreation: false, clubCreation: false, matchHistoryDays: 7, advancedStats: false, monthlyBonusBucks: 0, prioritySupport: false },
    club_member: { dailyDrip: 8, adFree: true, roomCreation: true, clubCreation: true, matchHistoryDays: 30, advancedStats: true, monthlyBonusBucks: 50, prioritySupport: true }
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    isClubMember,
    isPremium,
    tierFeatures: tierFeatures[user?.membershipTier || 'free'] || tierFeatures.free,
    signup,
    signin,
    signout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Idle warning banner */}
      {showIdleWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#92400e',
          color: '#fef3c7',
          textAlign: 'center',
          padding: '10px 16px',
          fontSize: 14,
          fontWeight: 600,
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}>
          You will be signed out in 2 minutes due to inactivity.
          <button
            onClick={() => setShowIdleWarning(false)}
            style={{
              marginLeft: 16,
              background: '#fef3c7',
              color: '#92400e',
              border: 'none',
              borderRadius: 4,
              padding: '4px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            I'm still here
          </button>
        </div>
      )}
      {/* Daily reward toast */}
      {dailyRewardToast && (
        <div style={{
          position: 'fixed',
          top: showIdleWarning ? 50 : 12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #1a3a1a, #1c3d2a)',
          border: '1px solid #3fb950',
          color: '#3fb950',
          padding: '12px 24px',
          borderRadius: 10,
          fontSize: 15,
          fontWeight: 600,
          zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          animation: 'fadeInDown 0.3s ease-out'
        }}>
          <img src="/bucks-icon.svg" alt="" style={{ width: 22, height: 22 }} />
          <span>+{dailyRewardToast.amount} Bucks</span>
          <span style={{ color: '#8b949e', fontSize: 12, fontWeight: 400 }}>
            Daily reward{dailyRewardToast.streak >= 7 ? ' (7+ day streak!)' : dailyRewardToast.streak > 1 ? ` (${dailyRewardToast.streak}-day streak)` : ''}
          </span>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
